import { DirectUpload } from '@rails/activestorage';
import { getResources } from 'atoms/root';
import { atom } from 'jotai';
import { atomWithReducer } from 'jotai/utils';
import { FileUploadStatus } from 'lib/types';

// Maximum file size for upload (500MB)
export const MAX_FILE_SIZE = 500 * 1024 * 1024;

const OVERSIZE_FILE_ERROR_MESSAGE = `File size exceeds the maximum limit of ${
  MAX_FILE_SIZE / (1024 * 1024)
}MB`;

const UPLOAD_FAILED_MESSAGE = 'Failed to upload file. Please try again.';

// Interface to track file upload state
export interface FileAtomState {
  status: FileUploadStatus;
  name?: string;
  file?: string;
  invalid?: boolean;
  error?: string;
}

/**
 * One entry in a multi-file upload list. Extends FileAtomState with the
 * list-management fields needed to address individual entries from the
 * setter actions (`localId`) and to filter rows from the visible list and
 * save payload without removing them from state (`hidden`).
 */
export interface MultiFileEntry extends FileAtomState {
  localId: string;
  hidden?: boolean;
  byteSize?: number;
  createdAt?: string;
}

/**
 * Setter action shape for {@link createMultiFileAtom}.
 * - `addNew`: kick off DirectUpload for each File; append entries.
 * - `hide`: flip an entry's `hidden` flag to `true` (entry stays in array).
 * - `reset`: clear the list.
 */
export type MultiFileAction =
  | { type: 'addNew'; files: File[] }
  | { type: 'hide'; localId: string }
  | { type: 'reset' };

export function createFileAtom() {
  // Base file upload atom
  const fileBaseAtom = atom<FileAtomState>({
    status: 'edit',
    name: undefined,
    file: undefined,
    invalid: false,
  });

  // File upload atom with DirectUpload implementation
  return atom<FileAtomState, [File | undefined], void>(
    (get) => get(fileBaseAtom),
    (get, set, file) => {
      if (!file) {
        set(fileBaseAtom, { status: 'edit' });
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        set(fileBaseAtom, {
          status: 'error',
          invalid: true,
          error: OVERSIZE_FILE_ERROR_MESSAGE,
        });
        return;
      }

      // Get directUploads URL from resources
      const resources = getResources(get);
      const url = resources?.directUploads.href;
      if (!url) return;

      // Create DirectUpload instance
      const directUpload = new DirectUpload(file, url);

      // Set uploading status
      set(fileBaseAtom, {
        status: 'uploading',
        name: file.name,
        invalid: false,
      });

      // Perform the upload
      directUpload.create((error, blob) => {
        if (error) {
          set(fileBaseAtom, {
            status: 'error',
            invalid: true,
            error: UPLOAD_FAILED_MESSAGE,
          });
        } else {
          set(fileBaseAtom, {
            status: 'complete',
            file: blob.signed_id,
            name: file.name,
            invalid: false,
          });
        }
      });
    }
  );
}

/**
 * Internal action shape consumed by {@link multiFileReducer}. Exported only
 * so the reducer is directly unit-testable as a pure function — consumers
 * should dispatch the public {@link MultiFileAction} into the orchestrator
 * atom returned by {@link createMultiFileAtom}.
 * @internal
 */
export type MultiFileReducerAction =
  | { type: 'append'; entries: MultiFileEntry[] }
  | { type: 'updateById'; localId: string; patch: Partial<MultiFileEntry> }
  | { type: 'hide'; localId: string }
  | { type: 'reset' };

/**
 * Pure reducer for the multi-file upload list. Every action returns the
 * same `prev` reference when the dispatch is a no-op (empty `append`,
 * empty `reset`, or `hide` / `updateById` targeting an unknown localId)
 * so subscribers don't re-render. Exported for direct unit testing.
 * @internal
 * @param {MultiFileEntry[]} prev - Previous list state.
 * @param {MultiFileReducerAction} action - Action to apply.
 * @return {MultiFileEntry[]} Next list state (or `prev` reference on no-op).
 */
export const multiFileReducer = (
  prev: MultiFileEntry[],
  action: MultiFileReducerAction
): MultiFileEntry[] => {
  switch (action.type) {
    case 'append':
      if (action.entries.length === 0) return prev;
      return [...prev, ...action.entries];
    case 'updateById':
      if (!prev.some((e) => e.localId === action.localId)) return prev;
      return prev.map((e) => {
        return e.localId === action.localId ? { ...e, ...action.patch } : e;
      });
    case 'hide':
      if (!prev.some((e) => e.localId === action.localId)) return prev;
      return prev.map((e) => {
        return e.localId === action.localId ? { ...e, hidden: true } : e;
      });
    case 'reset':
      return prev.length === 0 ? prev : [];
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
};

/**
 * Builds a {@link MultiFileEntry} from a raw File — generates the localId,
 * validates against MAX_FILE_SIZE, and produces an `'error'` entry for
 * oversize files or an `'uploading'` entry for valid ones. The caller is
 * responsible for kicking off DirectUpload when the resulting entry has
 * `status === 'uploading'`.
 * @param {File} file - The raw File object selected by the user.
 * @return {MultiFileEntry} The constructed entry.
 */
const buildMultiFileEntry = (file: File): MultiFileEntry => {
  const localId = crypto.randomUUID();
  if (file.size > MAX_FILE_SIZE) {
    return {
      localId,
      status: 'error',
      invalid: true,
      name: file.name,
      error: OVERSIZE_FILE_ERROR_MESSAGE,
    };
  }
  return {
    localId,
    status: 'uploading',
    name: file.name,
    invalid: false,
    byteSize: file.size,
    createdAt: new Date().toISOString(),
  };
};

/**
 * Factory that returns a Jotai atom holding a list of file upload entries.
 * Each entry tracks its own DirectUpload lifecycle independently. Shares
 * `MAX_FILE_SIZE` validation with {@link createFileAtom}.
 *
 * Architecture: a private `atomWithReducer`-backed state atom holds the
 * list; the returned orchestrator atom translates public actions into
 * reducer dispatches and kicks off DirectUpload side effects.
 *
 * @return {Atom<MultiFileEntry[], [MultiFileAction], void>} The multi-file atom
 */
export function createMultiFileAtom() {
  const filesAtom = atomWithReducer<MultiFileEntry[], MultiFileReducerAction>(
    [],
    multiFileReducer
  );

  return atom<MultiFileEntry[], [MultiFileAction], void>(
    (get) => get(filesAtom),
    (get, set, action) => {
      switch (action.type) {
        case 'addNew': {
          if (action.files.length === 0) return;
          const url = getResources(get)?.directUploads.href;
          if (!url) return;

          const prepared = action.files.map((file) => ({
            file,
            entry: buildMultiFileEntry(file),
          }));

          set(filesAtom, {
            type: 'append',
            entries: prepared.map((p) => p.entry),
          });

          prepared.forEach(({ file, entry }) => {
            if (entry.status === 'error') return;
            new DirectUpload(file, url).create((error, blob) => {
              set(filesAtom, {
                type: 'updateById',
                localId: entry.localId,
                patch: error
                  ? {
                      status: 'error',
                      invalid: true,
                      error: UPLOAD_FAILED_MESSAGE,
                    }
                  : {
                      status: 'complete',
                      file: blob.signed_id,
                      invalid: false,
                    },
              });
            });
          });
          break;
        }
        case 'hide':
          set(filesAtom, { type: 'hide', localId: action.localId });
          break;
        case 'reset':
          set(filesAtom, { type: 'reset' });
          break;
        default: {
          const _exhaustive: never = action;
          return _exhaustive;
        }
      }
    }
  );
}
